// src/components/NewsPanel.jsx
// 미국주식 종목별 최근 48시간 주요 뉴스. 점수 필터로 주가 영향 큰 것만.
// 헤드라인은 영어 원문 (Finnhub).

import React, { useEffect, useState } from "react";

async function streamNews(holdings, onItem) {
  const symbols = holdings.map(h => `${h.ticker}:${h.market}`).join(",");
  const res = await fetch(`/api/news?symbols=${encodeURIComponent(symbols)}`);
  if (!res.ok || !res.body) throw new Error(`news fetch failed: ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      try { onItem(JSON.parse(line)); } catch {}
    }
  }
  const tail = buf.trim();
  if (tail) { try { onItem(JSON.parse(tail)); } catch {} }
}

function timeAgo(epoch) {
  const diff = Math.max(0, Date.now() / 1000 - epoch);
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return `${Math.floor(diff / 86400)}일 전`;
}

export default function NewsPanel({ holdings }) {
  const [news, setNews] = useState({});
  const [loading, setLoading] = useState(true);
  const nameOf = (t) => (holdings.find(h => String(h.ticker) === String(t))?.name) || t;

  useEffect(() => {
    if (!holdings || holdings.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setNews({});
    streamNews(holdings, ({ ticker, news: items }) => {
      if (cancelled) return;
      let list = items || [];
      // KR: 네이버 종목피드에 섞인 일반 시장뉴스 제거 — 종목명이 헤드라인에 있는 것만
      const h = holdings.find(x => String(x.ticker) === String(ticker));
      const isKR = h && ["KR", "KS", "KQ"].includes(String(h.market || "").toUpperCase());
      if (isKR && h?.name) list = list.filter(n => (n.headline || "").includes(h.name));
      if (list.length > 0) setNews(prev => ({ ...prev, [ticker]: list }));
    })
      .catch(err => console.error("news error:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [holdings]);

  // 가장 최근 뉴스가 위로 오도록 종목 정렬
  const tickers = Object.keys(news).sort((a, b) => {
    const ta = Math.max(...news[a].map(n => n.datetime));
    const tb = Math.max(...news[b].map(n => n.datetime));
    return tb - ta;
  });
  const total = tickers.reduce((sum, t) => sum + news[t].length, 0);

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>주요 뉴스</h3>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
            최근 48시간 · 보유종목(국내 네이버·미국 Finnhub)
          </div>
        </div>
        <span style={{ fontSize: 12, color: "#aaa" }}>
          {loading ? "불러오는 중..." : `${tickers.length}개 종목 · ${total}건`}
        </span>
      </div>

      {!loading && tickers.length === 0 ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: 24, fontSize: 13 }}>
          주가에 영향을 줄 만한 뉴스 없음
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {tickers.map(ticker => (
            <div key={ticker} style={{ display: "flex", gap: 12, borderTop: "1px solid #f0f0f0", padding: "10px 0" }}>
              <div style={{ minWidth: 72, maxWidth: 84, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameOf(ticker)}</div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                {news[ticker].map((n, i) => (
                  <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" style={{
                    textDecoration: "none", color: "inherit", display: "block",
                  }}>
                    <div style={{ fontSize: 13, color: "#333", lineHeight: 1.4 }}>{n.headlineKo || n.headline}</div>
                    {n.headlineKo && (
                      <div style={{ fontSize: 10, color: "#bbb", marginTop: 1, lineHeight: 1.3 }}>{n.headline}</div>
                    )}
                    <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
                      {n.source} · {timeAgo(n.datetime)}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
